// 11 §10.4 Level-up (plate C), docs/119: portrait, the L → L+1 banner, exact XP, then what the level
// brings — the class and species steps from the sim's own walk. Gains are shown as they are; a
// choice (the path at 3, a spell or technique from the library, a style, a heritage) is a row of
// cards with the default marked, and taking the level sends the picks (`take_level`). Skill points
// are banked and spent on the hero's Path page. Past level 20 a level is a laurel: three epic boons,
// pick one (`choose_level_up`). `LevelUpCards` is the shared body (Aftermath inline and the modal
// opened from the roster badge); no `%` anywhere in the copy.
import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Display, Eyebrow, Meter, Modal, Portrait, Stamp } from '../components';
import { CLASS_NAMES } from '../../core/sim/adapt';
import { rpc } from '../../core/net/reducers';
import { useHeroCallings, useHeroProgress, useMyLevelUpOffers, useWorldHeroes } from '../../core/subscriptions/hooks';
import { useSession } from '../../core/stores/session';
import { useOverlay } from '../overlay';
import { playSfx } from '../../core/audio';
import { rewardSfx } from '../sfx';
import { heroVoiceOf, playHeroLine } from '../../core/audio/voices';
import { useHeroVoices } from '../../core/subscriptions/hooks';
import { classResource } from './forge';
import { featureLines, featureOf, heroWalk, isChoice, optionCard, picksFlat, stepKind, stepTitle, TRACK_TOP, xpToReach, type ProgressRow, type SlotView, type Walk } from '../../core/sim/progression';
import './levelup.css';

type LevelUpOffer = ReturnType<typeof useMyLevelUpOffers>[number];
type Hero = ReturnType<typeof useWorldHeroes>[number];

export function LevelUpCards({ offer, hero, onChosen }: { offer: LevelUpOffer; hero?: Hero; onChosen?: () => void }) {
  const voices = useHeroVoices(hero?.worldId);
  const voiceRow = hero ? voices.find((v) => v.heroId === hero.id) : undefined;
  // docs/34: the fanfare, then the hero's own word (only this player is at this card)
  useEffect(() => {
    playSfx(rewardSfx('ui.levelup'));
    playSfx('ui.dice', { delayMs: 650, gain: 0.7 });   // docs/44: the offer's cards are shuffled and dealt
    if (!hero) return;
    const t = setTimeout(() => playHeroLine({ id: hero.id, classId: CLASS_NAMES[hero.class] ?? 'fighter', species: hero.species, voice: heroVoiceOf(hero.id, voiceRow) }, 'level'), 1400);
    return () => clearTimeout(t);
  }, [offer.id]);   // once per offer
  const classId = CLASS_NAMES[hero?.class ?? 0] ?? 'fighter';
  const floor = xpToReach(offer.toLevel - 1);
  const next = xpToReach(offer.toLevel);
  return (
    <div className="fk-levelup" data-testid="levelup" data-to-level={offer.toLevel}>
      <div className="fk-levelup__hero">
        <Portrait heroId={hero?.id} species={hero?.species} frame={hero?.oryxFrame} classId={classId} scale={4} glow title={hero?.name} />
        <div className="fk-levelup__banner">
          <Eyebrow>{classId}</Eyebrow>
          <Display size="lg" gold>{hero?.name ?? 'Hero'}</Display>
          <Stamp small sub={offer.toLevel > TRACK_TOP ? 'a laurel — pick one boon' : 'every number is exact'}>L{offer.toLevel - 1} → L{offer.toLevel}</Stamp>
          <Meter kind="xp" label="XP" value={Math.max(0, (hero?.xp ?? 0) - floor)} max={Math.max(1, next - floor)} format={() => `${hero?.xp ?? 0} xp`} size="sm" />
        </div>
      </div>
      {offer.toLevel > TRACK_TOP
        ? <Laurels offer={offer} onChosen={onChosen} />
        : hero ? <LevelSteps offer={offer} hero={hero} onChosen={onChosen} /> : null}
    </div>
  );
}

/** Past 20: three laurels drawn by the module (stored on the offer); pick one. */
function Laurels({ offer, onChosen }: { offer: LevelUpOffer; onChosen?: () => void }) {
  const [picked, setPicked] = useState<number>();
  const [busy, setBusy] = useState(false);
  const choose = async (c: number) => {
    if (busy) return;
    setPicked(c); setBusy(true);
    const r = await rpc.chooseLevelUp(offer.id, c);
    setBusy(false);
    if (r.ok) { playSfx('cine.shimmer'); onChosen?.(); } else setPicked(undefined);   // docs/44: the chosen card glints
  };
  return (
    <>
      <p className="fk-levelup__lead">Past level 20 every level is a laurel: one epic boon, and a skill point to spend on the Path page.</p>
      <div className="fk-levelup__cards">
        {offer.cards.map((c, i) => {
          const f = featureOf(c);
          const isPicked = picked === c;
          return (
            <Card key={c} as="button" interactive index={i} selected={isPicked} dim={picked !== undefined && !isPicked} column className="fk-levelup__card fk-reveal fk-reveal--flip" data-testid={`levelup-card-${c}`} onClick={() => void choose(c)} disabled={busy} aria-pressed={isPicked}>
              <span className="fk-levelup__card-no" aria-hidden="true">{['I', 'II', 'III'][i] ?? i + 1}</span>
              <span className="fk-levelup__card-title">{f?.name ?? `Laurel ${c}`}</span>
              <span className="fk-levelup__card-text fk-levelup__card-blurb">{f?.blurb}</span>
              {f && featureLines(f).map((l) => <span key={l} className="fk-levelup__card-text">{l}</span>)}
              {isPicked && <Badge tone="gold" filled className="fk-levelup__chosen">{busy ? 'choosing…' : 'chosen'}</Badge>}
            </Card>
          );
        })}
      </div>
      {onChosen && <div className="fk-row fk-row--end"><Button variant="ghost" onClick={onChosen} disabled={busy}>Later</Button></div>}
    </>
  );
}

/** Levels 2–20: what the class track and the heritage bring at this level, with its choices. */
function LevelSteps({ offer, hero, onChosen }: { offer: LevelUpOffer; hero: Hero; onChosen?: () => void }) {
  const level = offer.toLevel;
  const calling = useHeroCallings(hero.worldId).find((c) => c.heroId === hero.id);
  const row = useHeroProgress(hero.worldId).find((r) => r.heroId === hero.id);
  const [picked, setPicked] = useState<Map<number, number>>(new Map());
  const [busy, setBusy] = useState(false);
  const classId = CLASS_NAMES[hero.class] ?? 'fighter';
  const resource = classResource(hero.class);
  // the walk with this screen's picks laid over the stored ones (a path picked here changes the
  // path feature shown beside it; a spell learned here leaves the next Learn's list)
  const walk: Walk | undefined = useMemo(() => {
    const base: ProgressRow = { picks: [...(row?.picks ?? [])], training: [...(row?.training ?? [])], talents: [...(row?.talents ?? [])], laurels: [...(row?.laurels ?? [])] };
    const extra = [...picked.entries()].flatMap(([slot, v]) => [level, slot, v]);
    try { return heroWalk(hero.class, hero.species, level, calling?.pick ?? 0, calling?.spells ?? [], { ...base, picks: [...base.picks, ...extra] }); } catch { return undefined; }
  }, [hero.class, hero.species, level, calling, row, picked]);
  const before: Walk | undefined = useMemo(() => {
    try { return heroWalk(hero.class, hero.species, level - 1, calling?.pick ?? 0, calling?.spells ?? [], row); } catch { return undefined; }
  }, [hero.class, hero.species, level, calling, row]);
  const slots = (walk?.slots ?? []).filter((s) => s.level === level);
  const gained = (walk?.earned ?? 0) - (before?.earned ?? 0);
  const take = async () => {
    if (busy) return;
    setBusy(true);
    const r = await rpc.takeLevel(offer.id, picksFlat(picked));
    setBusy(false);
    if (r.ok) {
      playSfx('cine.shimmer');
      onChosen?.();
      useOverlay.getState().openHeroPath(hero.id);
    }
  };
  return (
    <>
      <div className="fk-levelup__steps" data-testid="levelup-steps">
        {slots.map((s) => (
          <StepBlock key={s.slot} s={s} classId={classId} resource={resource} picked={picked.get(s.slot)} onPick={(v) => setPicked(new Map(picked).set(s.slot, v))} disabled={busy} />
        ))}
      </div>
      <div className="fk-levelup__points" data-testid="levelup-points">
        <span className="fk-levelup__points-gain">+{gained} skill point{gained === 1 ? '' : 's'}</span>
        <span className="fk-levelup__points-bank">{walk?.banked ?? 0} to spend after this level, on training and talents — or bank them for later</span>
      </div>
      <div className="fk-row fk-row--end">
        {onChosen && <Button variant="ghost" onClick={onChosen} disabled={busy}>Later</Button>}
        <Button variant="primary" onClick={() => void take()} loading={busy} data-testid="levelup-take">Take level {level}</Button>
      </div>
    </>
  );
}

function StepBlock({ s, classId, resource, picked, onPick, disabled }: { s: SlotView; classId: string; resource: string; picked?: number; onPick: (v: number) => void; disabled: boolean }) {
  const kind = stepKind(s.step);
  if (kind === 'points') return null;   // summed in the points line below
  const title = stepTitle(s, classId);
  if (!isChoice(s)) {
    const id = s.value ?? s.options[0];
    if (id === undefined || id === null) return null;
    const c = optionCard(s, id, classId, resource);
    return (
      <section className="fk-levelup__step" data-testid={`levelup-step-${s.slot}`} data-kind={kind}>
        <Eyebrow>{title}</Eyebrow>
        <Card column className="fk-levelup__feature fk-reveal fk-reveal--flip">
          <span className="fk-levelup__card-title">{c.name} <Badge tone="gold">new</Badge></span>
          {c.blurb && <span className="fk-levelup__card-text fk-levelup__card-blurb">{c.blurb}</span>}
          {c.lines.map((l) => <span key={l} className="fk-levelup__card-text">{l}</span>)}
        </Card>
      </section>
    );
  }
  const current = picked ?? s.value ?? undefined;
  const many = s.options.length > 6;
  return (
    <section className="fk-levelup__step" data-testid={`levelup-step-${s.slot}`} data-kind={kind}>
      <Eyebrow>{title}{s.options.length > 1 ? ` · ${s.options.length} to choose from` : ''}</Eyebrow>
      <div className={`fk-levelup__options ${many ? 'fk-levelup__options--many' : ''}`} role="radiogroup" aria-label={title}>
        {s.options.map((id, i) => {
          const c = optionCard(s, id, classId, resource);
          const on = current === id;
          return (
            <Card key={id} as="button" interactive index={Math.min(i, 8)} selected={on} column className="fk-levelup__card fk-levelup__option" role="radio" aria-checked={on}
              data-testid={`levelup-option-${id}`} onClick={() => onPick(id)} disabled={disabled}>
              <span className="fk-levelup__card-title">{c.name}</span>
              <span className="fk-levelup__tags">
                {i === 0 && picked === undefined && <Badge tone="grey">suggested</Badge>}
                {c.from === s.level && <Badge tone="gold">new at level {s.level}</Badge>}
                {c.tag && <Badge tone="plum">{c.tag}</Badge>}
              </span>
              {c.blurb && <span className="fk-levelup__card-text fk-levelup__card-blurb">{c.blurb}</span>}
              {c.lines.map((l) => <span key={l} className="fk-levelup__card-text">{l}</span>)}
            </Card>
          );
        })}
      </div>
    </section>
  );
}

/** Opened from a roster "Level up!" badge; closes itself once the offer row is gone. */
export function LevelUpModal() {
  const offerId = useOverlay((s) => s.levelUp);
  const close = useOverlay((s) => s.closeLevelUp);
  const worldId = useSession((s) => s.worldId);
  const offer = useMyLevelUpOffers().find((o) => o.id === offerId);
  const heroes = useWorldHeroes(worldId);
  const hero = offer ? heroes.find((h) => h.id === offer.heroId) : undefined;
  return (
    <Modal open={!!offer} onClose={close} eyebrow="Level up" title={offer ? `${hero?.name ?? 'A hero'} reaches level ${offer.toLevel}` : ''} size="wide" testId="levelup-modal">
      {offer && <LevelUpCards offer={offer} hero={hero} onChosen={close} />}
    </Modal>
  );
}
